import { reportSsrDataLoadFailure } from '@/app/ssrDataStatus'
import type {
  Product,
  ProductDetails,
  ProductNutritionalData,
  ProductStock
} from '@/modules/types/product/ProductSchema.types'
import { translate } from '@/plugins/i18n.js'
import { extractCollectionPrefix } from '@/services/helpers/CollectionUrlHelper'
import { useCollectionPageStore } from '@/stores/collectionPage'
import { defineStore, getActivePinia, type Pinia } from 'pinia'
import type { Router } from 'vue-router'

/**
 * The v2 product as the store holds it: the API payload once loaded, over the
 * empty pre-fetch shape the templates render skeletons from. The pre-fetch
 * shape nulls the ids and carries legacy placeholder keys (general, design,
 * price, legal) that the real payload does not have.
 */
export type PdpApiProduct = Omit<
  Partial<Product>,
  'product_id' | 'details' | 'description'
> & {
  product_id: number | null
  details: Partial<ProductDetails>
  /** A string once loaded; the legacy pre-fetch shape seeds an empty object */
  description?: string | null | Record<string, unknown>
  /** Legacy pre-fetch placeholders, absent from a loaded payload */
  general?: Record<string, unknown>
  design?: Record<string, unknown>
  price?: Record<string, unknown>
  legal?: { reference_unit: string; reference_unit_contents: number }
}

/**
 * The v1 payload — deliberately loose: the endpoint is a temporary source for
 * awards/bundle_items/expert_reviews until VIP-10085 & VIP-10086 replace it,
 * and has no schema types.
 */
export interface PdpApiV1Product {
  description: Record<string, unknown>
  details: Record<string, unknown>
  design: Record<string, unknown>
  expert_reviews: Record<string, unknown>
  [key: string]: unknown
}

/** The page-root meta endpoint's payload (personalized bits, ratings, GA data). */
export interface PdpMetaProduct {
  milesandmore: Record<string, unknown>
  recommendation: Record<string, unknown>
  [key: string]: unknown
}

export interface PdpBundleReference {
  product_id: number
  campaign_id: number
}

interface PrefixedImage {
  src: string
  retina_src: string
}

/** One entry of UtilityHelper.BACKGROUNDS: a 1x/2x image pair. */
interface BackgroundImageSet {
  X: string
  XX: string
}

/** Shape of ProductHelper.createPriceObject; the pre-fetch default nulls a few legacy keys. */
export interface PdpPriceObject {
  finalPrice?: number
  finalPriceFormatted?: string | null
  originalPrice?: number
  unitPrice?: string | null
  savings?: string | null
  validUntil?: string | null
  showUnitPrice?: boolean
  currency?: string | null
  showCountdown?: boolean
  specialPrice?: number | null
  specialPriceFormatted?: string | null
  specialPriceFrom?: string | null
  specialPriceTo?: string | null
  /** Legacy pre-fetch placeholder */
  price?: null
}

/** Values refreshCalculatedFields derives from the loaded product. */
export interface PdpCalculated {
  isSimple: boolean
  isBundle: boolean
  backToReferrer: string
  backToReferrerForced: boolean
  backToReferrerTitle: string
  /** Cookie value; only read for truthiness */
  mmEnabled: string | boolean | undefined
  isVisible: boolean | undefined
  preSelectedQty: number | null
  priceObject: PdpPriceObject
  manufacturerUrl: string
  smallImage: PrefixedImage | ''
  smallImageSrcPrefixed: string
  imageSrc: string
  heroImageSrc: string
  bgImageSet: BackgroundImageSet | ''
  tags: string[]
  nutritionalNotes: string
  /** Only present once refreshCalculatedFields ran; the pre-fetch default omits it */
  nutritionalData?: Partial<ProductNutritionalData>
  countries: string[]
  extraBanner: boolean
  volume: string
  safetyNote: string | null | undefined
}

interface PdpStoreState {
  metaProduct: PdpMetaProduct
  apiProduct: PdpApiProduct
  apiV1Product: PdpApiV1Product
  apiBundleIdsForProduct: PdpBundleReference[]
  calculated: PdpCalculated
  manufacturer: { image_url: string | null; maker_description: string | null }
  updating: boolean
  /**
   * True while the store holds a product a server render loaded and the
   * client has not consumed it yet (VIP-12038). Serialized into the SSR
   * payload; consumeSsrHydration() clears it on first client navigation so
   * exactly one refetch is skipped.
   */
  ssrHydrated: boolean
  /**
   * True from the server render until the client has resolved the price for
   * its own session (VIP-12351): the API resolves `final_price` per session
   * (admins see the "endless special price" outside its window), so the
   * anonymous server render must not show a price at all. Serialized into
   * the SSR payload, so server and client render the same skeleton. Drives
   * the price skeletons alone — the rest of the rendered page stays put,
   * unlike `updating`.
   */
  priceUpdating: boolean
}

export const usePdpStore = defineStore('pdp', {
  state: (): PdpStoreState => ({
    metaProduct: _initialMetaProduct(),
    apiProduct: _initialApiProduct(),
    apiV1Product: _initialApiProductV1(),
    apiBundleIdsForProduct: _initialApiBundleIdsForProduct(),
    calculated: _initialCalculatedState(),
    manufacturer: _initialManufacturer(),
    updating: false,
    ssrHydrated: false,
    priceUpdating: false
  }),
  getters: {
    getFirstAvailableStock: ({ apiProduct: { stocks } }): ProductStock | null =>
      stocks?.find((stock) => stock.is_enabled && stock.is_in_stock) || null,
    getApiProduct: ({ apiProduct }): PdpApiProduct => apiProduct,
    getApiV1Product: ({ apiV1Product }): PdpApiV1Product => apiV1Product,
    getBundleProductIds: ({ apiBundleIdsForProduct }): PdpBundleReference[] =>
      apiBundleIdsForProduct,
    getMetaProduct: ({ metaProduct }): PdpMetaProduct => metaProduct,
    getCalculated: ({ calculated }): PdpCalculated => calculated,
    isUpdating: ({ updating }): boolean => updating,
    isPriceUpdating: ({ priceUpdating }): boolean => priceUpdating,
    isVisible: ({ calculated: { isVisible } }): boolean | undefined => isVisible
  },
  actions: {
    /**
     * Server-side data loader (VIP-12038): loads the anonymous PDP payload —
     * the two product representations, nothing else. Tracking, the
     * meta-product and personalization stay client concerns. When the v2
     * answer resolves to a redirect (redirect_url, OPC product),
     * fetchApiProduct replaces the route and leaves the store empty; the
     * server entry surfaces that as RenderResult.redirectTo. A failed load is
     * swallowed: the shell renders with skeletons, ssrHydrated stays false,
     * and the client refetches on hydration exactly as it does today.
     */
    async loadProductForServerRender({
      productId,
      campaignId
    }: {
      productId: number | string
      campaignId: number | string
    }): Promise<void> {
      // Independent requests — the client fetches them sequentially, but a
      // render should not pay two round trips. allSettled, not all: an early
      // rejection must not resolve serverPrefetch while the surviving fetch
      // keeps mutating this request's store mid-render.
      const [v1Result, v2Result] = await Promise.allSettled([
        this.fetchApiV1Product(productId, campaignId),
        this.fetchApiProduct(productId, campaignId)
      ])

      // Only v2 decides whether this render carries a product at all: it holds
      // the name, price, description, details, awards and bundle items, and
      // the Product microdata is built from them. Losing v1 costs the customer
      // rating and the expert reviews — the rating only ever lives in v1, so
      // `customer_rating` in the v2 payload is not a substitute (it exists but
      // is not populated; verified on staging, where v1 reports 3.2/139 for a
      // product v2 reports as unrated).
      //
      // Two consequences, both deliberate:
      //
      // The render proceeds. Discarding a good v2 payload because v1 failed
      // made the server stricter than the browser, which fetches the two
      // independently and tolerates a v1 failure — a page with everything but
      // its rating beats an empty skeleton.
      //
      // It is still reported, so the response stays uncacheable. The rating
      // feeds the AggregateRating microdata, so a page without it is
      // SEO-degraded even though it looks complete, and caching that for a TTL
      // would hand crawlers a version with no stars.
      if (v1Result.status === 'rejected') {
        reportSsrDataLoadFailure(v1Result.reason)
        this.apiV1Product = _initialApiProductV1()
      }

      if (v2Result.status === 'rejected') {
        // Reported, not thrown: the shell is still a valid page, but the
        // caller must know it carries no product so the response is not
        // cached (VIP-12038).
        reportSsrDataLoadFailure(v2Result.reason)
        // A half-loaded store must neither render nor serialize: reset so the
        // shell shows consistent skeletons and the client refetches as today.
        this.apiProduct = _initialApiProduct()
        this.apiV1Product = _initialApiProductV1()
        this.calculated = _initialCalculatedState()
        return
      }
      // Empty ids mean fetchApiProduct resolved to a redirect instead of a
      // product — nothing was loaded, so there is nothing to hydrate.
      this.ssrHydrated = this.apiProduct.product_id != undefined
      // The rendered price is the anonymous one; the client decides after
      // hydration whether its session gets a different one (VIP-12351).
      this.priceUpdating = this.ssrHydrated
    },
    /**
     * Client-side follow-up to a hydrated entry (VIP-12038): patches the one
     * calculated field whose input the anonymous server render cannot know —
     * the Miles&More cookie. Everything else in `calculated` derives from the
     * product payload and the URL, which server and client share.
     */
    async applyClientOnlyCalculatedFields(): Promise<void> {
      const { CookieHelper, COOKIES } = await _getModules()
      this.calculated.mmEnabled = CookieHelper.getCookie(
        COOKIES.MILES_AND_MORE_ENABLED
      )
    },
    /**
     * One-shot check the client runs on entering the PDP (VIP-12038):
     * true exactly once, when the store still holds the server-loaded
     * product for this route — the caller then skips the initial refetch.
     * Every later navigation (even back to the same product) refetches — the
     * responses are cacheable (v2 products ship max-age=86400, invalidated
     * through the Cloudflare purge queue), so this is about asking again
     * rather than about guaranteed-fresh prices.
     */
    consumeSsrHydration({
      productId,
      campaignId
    }: {
      productId: number | string
      campaignId: number | string
    }): boolean {
      if (!this.ssrHydrated) return false
      this.ssrHydrated = false
      return (
        this.apiProduct.product_id === Number(productId) &&
        this.apiProduct.campaign_id === Number(campaignId)
      )
    },
    /**
     * Client-side follow-up to a hydrated entry (VIP-12351): resolves the
     * price the server left as a skeleton. Waits for the session the app is
     * establishing on boot; an admin gets the session-resolved payload
     * (the "endless special price" preview the anonymous render can never
     * carry), everyone else simply gets the rendered price revealed. Resolves
     * to whether a refetch ran.
     *
     * Swaps the payload in place via fetchApiProduct rather than going
     * through refreshProduct: that one clears the store and raises
     * `updating`, which drops the already rendered page back into its
     * skeletons for the round trip.
     */
    async refreshProductForAdminSession({
      productId,
      campaignId
    }: {
      productId: number | string
      campaignId: number | string
    }): Promise<boolean> {
      const { useCustomerStore } = await _getModules()
      // Client-only: the browser has exactly one pinia, so the active
      // instance is the right one here (unlike the server-render paths).
      const customerStore = useCustomerStore()
      try {
        await customerStore.waitUntilPending()
        if (!customerStore.customerIsAdmin) return false

        await this.fetchApiProduct(productId, campaignId)
        return true
      } finally {
        // Whatever happened, the price must not stay a skeleton
        this.priceUpdating = false
      }
    },
    async refreshProduct({
      productId,
      campaignId
    }: {
      productId: number | string
      campaignId: number | string
    }): Promise<void> {
      const { ApiController } = await _getModules()

      this.updating = true
      // A cold load resolves the price itself; `updating` covers the skeleton
      this.priceUpdating = false
      this.metaProduct = _metaProduct()
      this.calculated = _initialCalculatedState()
      this.apiProduct = _initialApiProduct()
      this.apiV1Product = _initialApiProductV1()
      this.manufacturer = _initialManufacturer()

      try {
        await this.fetchApiV1Product(productId, campaignId)
        await this.fetchApiProduct(productId, campaignId)
        this.fetchMetaProduct(productId, campaignId)
      } catch (e) {
        ApiController.logFrontendError(e, 'pdp_fetch_product_failed', 'notice')
        console.error(e)
        const url = this.router.resolve(this.router.currentRoute.value).fullPath
        this.router.push(`/error?original=${url}`)
      }
      this.updating = false
    },
    async refreshCalculatedFields(apiProduct: Product): Promise<void> {
      // Resolved synchronously at action entry, where pinia has set the
      // correct active instance — after an await, the module-global
      // activePinia may belong to a concurrent SSR render (VIP-12038).
      const collectionPageStore = useCollectionPageStore()
      const {
        UtilityHelper,
        CookieHelper,
        COOKIES,
        PRODUCT_TYPES,
        BACKGROUNDS,
        SiteConfig,
        createPriceObject
      } = await _getModules()

      const isSimple =
        apiProduct.type === PRODUCT_TYPES.SIMPLE ||
        apiProduct.bundle_items?.length === 1
      const isBundle = apiProduct.type === PRODUCT_TYPES.BUNDLE

      const bgImageSet = isBundle ? BACKGROUNDS.BUNDLE : BACKGROUNDS.SIMPLE

      const collectionPrefix = extractCollectionPrefix(
        this.router.currentRoute.value.path
      )
      const collectionSlug = collectionPrefix?.replace(/-col-c\d+$/, '') ?? ''
      const pageUrlSlug = (collectionPageStore.entity?.page_url ?? '')
        .replace(/^\//, '')
        .replace(/-col-c\d+$/, '')
      const matchedCollectionEntity =
        collectionPrefix && pageUrlSlug === collectionSlug
          ? collectionPageStore.entity
          : null
      const backToReferrer = collectionPrefix
        ? '/' + collectionPrefix
        : SiteConfig.pageRoot + apiProduct.back_to_referrer_link
      const backToReferrerForced = !!collectionPrefix
      const backToReferrerTitle = matchedCollectionEntity?.title ?? ''
      const manufacturerUrl = SiteConfig.pageRoot + apiProduct.manufacturer_link

      const mmEnabled = CookieHelper.getCookie(COOKIES.MILES_AND_MORE_ENABLED)

      const prefixedImg = UtilityHelper.prefixImage(
        apiProduct.images.small_image,
        false
      )
      const prefixedHImg = UtilityHelper.prefixImage(
        apiProduct.images.image,
        true
      )

      const smallImageSrcPrefixed = UtilityHelper.prefixImage(
        apiProduct.images.small_image,
        false
      ).retina_src
      const imageSrc = UtilityHelper.generateSrcSet(
        prefixedImg.src,
        prefixedImg.retina_src
      )
      const heroImageSrc = UtilityHelper.generateSrcSet(
        prefixedHImg.src,
        prefixedHImg.retina_src
      )
      const smallImage = UtilityHelper.prefixImage(
        apiProduct.images.small_image
      )

      const tags = (apiProduct.tags ?? '').split(',')
      const isVisible = apiProduct.is_enabled

      // Number() is the unary + of the legacy JS — NaN and 0 both fall
      // through to the product's own pre-selected quantity
      const preSelectedQty =
        Number(this.router.currentRoute.value.query.qty) ||
        apiProduct.pre_selected_qty
      const priceObject = await createPriceObject(apiProduct)
      const nutritionalNotes = (apiProduct.details.nutritional_note ?? []).join(
        ', '
      )
      const nutritionalData = apiProduct.nutritional_data ?? {}
      const countries = apiProduct.details.country
        ? [apiProduct.details.country]
        : []
      const extraBanner =
        SiteConfig.siteId === 10000 &&
        this.router.currentRoute.value.path.includes('-vorpremiere')
      const volume = await _getVolume(apiProduct)
      const safetyNote = apiProduct.safety_note

      this.calculated = {
        smallImage,
        isSimple,
        isBundle,
        backToReferrer,
        backToReferrerForced,
        backToReferrerTitle,
        mmEnabled,
        isVisible,
        preSelectedQty,
        priceObject,
        manufacturerUrl,
        smallImageSrcPrefixed,
        imageSrc,
        heroImageSrc,
        bgImageSet,
        tags,
        nutritionalNotes,
        nutritionalData,
        countries,
        extraBanner,
        volume,
        safetyNote
      }
    },
    async fetchApiProduct(
      productId: number | string,
      campaignId: number | string
    ): Promise<void> {
      // Captured synchronously at action entry — after the network await
      // below, the module-global activePinia may already belong to a
      // concurrent SSR render (VIP-12038).
      const pinia = getActivePinia()
      const { ApiController, PDP_CONFIG } = await _getModules()

      const apiProduct: Product = await ApiController.fetchProduct(
        productId,
        campaignId,
        PDP_CONFIG
      )
      const { redirect_url: redirect } = apiProduct
      const redirectWithoutDomain = redirect?.replace(/https?:\/\/[^/]+/, '')

      if (
        redirectWithoutDomain &&
        redirectWithoutDomain !== this.router.currentRoute.value.fullPath
      ) {
        await this.router.replace(redirectWithoutDomain)
        return
      }

      // A true return means the product resolved to the OPC route — like the
      // redirect_url branch above, nothing must be stored for it.
      if (await _checkProductState(apiProduct, this.router, pinia)) return

      await this.refreshCalculatedFields(apiProduct)
      this.apiProduct = apiProduct
    },
    async fetchMetaProduct(
      productId: number | string,
      campaignId: number | string
    ): Promise<void> {
      const { TrackingHelper, ApiController } = await _getModules()

      void TrackingHelper.sendUtmParams({ productId, campaignId })

      TrackingHelper.productView(productId, campaignId)
      this.metaProduct = await ApiController.fetchMetaProduct(
        productId,
        campaignId
      )
    },
    async fetchApiV1Product(
      productId: number | string,
      campaignId: number | string
    ): Promise<void> {
      // this is a temporary solution to fetch the additional product data from the old API, till VIP-10085 & VIP-10086 are done
      const { ApiController, PDP_CONFIG_V1 } = await _getModules()

      this.apiV1Product = await ApiController.fetchApiV1Product(
        productId,
        campaignId,
        PDP_CONFIG_V1
      )
    },
    async fetchBundleIdsForProduct(): Promise<void> {
      try {
        const { ApiController } = await _getModules()
        const {
          params: { pId, cId }
        } = this.router.currentRoute.value

        const apiBundleIdsForProduct =
          await ApiController.fetchBundleIdsForProduct(pId, cId)

        if (apiBundleIdsForProduct && Array.isArray(apiBundleIdsForProduct)) {
          this.apiBundleIdsForProduct = apiBundleIdsForProduct
        } else {
          console.warn(
            `No bundle IDs returned for product ${pId} and campaign ${cId}.`
          )
          this.apiBundleIdsForProduct = []
        }
      } catch (error) {
        console.error('Failed to fetch bundle IDs for product:', error)
        this.apiBundleIdsForProduct = []
      }
    }
  }
})

/**
 * Gate a fetched product: resolves true when it redirected to the OPC route
 * (the caller must not store it), false when it may render, and throws for a
 * disabled product a non-admin must not see.
 */
const _checkProductState = async (
  { is_opc: isOpc, is_enabled: enabled }: Product,
  router: Router,
  pinia: Pinia | undefined
): Promise<boolean> => {
  const { useCustomerStore } = await _getModules()
  if (isOpc) {
    await router.replace(
      router.currentRoute.value.path.replace('-pdp-', '-opc-')
    )
    return true
  }
  if (enabled) return false

  // The caller's pinia, not the module-global one — see fetchApiProduct.
  const customerStore = useCustomerStore(pinia)
  await customerStore.waitUntilPending()
  if (customerStore.customerIsAdmin) return false

  throw new Error('Disabled product or OPC')
}

const _getVolume = async (product: Product): Promise<string> => {
  const { filters } = await _getModules()

  const typeTranslation = translate(_getUnitTypeKet(product.unit_type))
  const translation = translate(
    product.unit_type === 'piece' ? 'pdp_weight_per' : 'pdp_contents_per',
    typeTranslation
  )

  const contents = filters.float(product.reference_unit_contents)

  // The non-null assertion preserves the legacy JS exactly: a null unit on a
  // product with contents throws and surfaces as a failed product load
  return contents
    ? `${translation} ${contents} ${product.reference_unit!.toUpperCase()}`
    : ''
}

const _getUnitTypeKet = (unit: string | null): string => {
  if (unit === 'package') return 'fe_app_pdp_package'
  if (unit === 'piece') return 'pdp_attribute_slice'

  return 'fe_app_pdp_bottle'
}

const _initialMetaProduct = (): PdpMetaProduct => ({
  milesandmore: {},
  recommendation: {}
})

const _initialManufacturer = (): PdpStoreState['manufacturer'] => ({
  image_url: null,
  maker_description: null
})

const _initialApiProduct = (): PdpApiProduct => ({
  product_id: null,
  campaign_id: null,
  general: {},
  description: {},
  details: {},
  design: {},
  stocks: [],
  awards: [],
  price: {},
  legal: {
    reference_unit: '',
    reference_unit_contents: 0
  }
})

const _initialApiProductV1 = (): PdpApiV1Product => ({
  description: {},
  details: {},
  design: {},
  expert_reviews: {}
})
const _initialApiBundleIdsForProduct = (): PdpBundleReference[] => []

const _initialCalculatedState = (): PdpCalculated => ({
  isSimple: true,
  isBundle: false,
  backToReferrer: '',
  backToReferrerForced: false,
  backToReferrerTitle: '',
  mmEnabled: false,
  isVisible: true,
  preSelectedQty: null,
  priceObject: {
    price: null,
    savings: null,
    unitPrice: null,
    currency: null
  },
  manufacturerUrl: '',
  smallImage: '',
  smallImageSrcPrefixed: '',
  imageSrc: '',
  heroImageSrc: '',
  bgImageSet: '',
  tags: [],
  nutritionalNotes: '',
  countries: [],
  extraBanner: false,
  volume: '',
  safetyNote: ''
})

const _metaProduct = (): PdpMetaProduct => ({
  url: null,
  product_id: null,
  campaign_id: null,
  is_enabled: null,
  attribute_set: null,
  type: null,
  name: null,
  manufacturer: null,
  manufacturer_url: null,
  tags: null,
  image_src: null,
  image_retina_src: null,
  reason_why_one: null,
  successor_url: null,
  show_catch_phrase: null,
  ratings_count: null,
  ratings_average: null,
  heart_ratings: null,
  catch_phrase: null,
  awards: null,
  last_purchase_date: null,
  has_been_bought: null,
  rating: null,
  milesandmore: {},
  recommendation: {},
  price: null,
  wishlist_item_id: null,
  contents: null,
  image_hero_src: null,
  image_hero_retina_src: null,
  vendor: null,
  wine_alcohol_concentration: null,
  distributor: null,
  legals: null,
  legal_information_string: null,
  contains_sulfide: null,
  delivery: null,
  qty: null,
  ga_data: null,
  expert_reviews: null,
  wine_maker_products: null,
  characteristics: null,
  bundle_items: null,
  score_info: null
})

interface PdpModules {
  UtilityHelper: any
  TrackingHelper: any
  TranslationHelper: any
  ApiController: any
  COOKIES: Record<string, string>
  PRODUCT_TYPES: Record<string, string>
  SiteConfig: any
  CookieHelper: any
  AnalyticsItemFactory: any
  GA4Controller: any
  /** The store factory, not an instance — callers pass their request's pinia */
  useCustomerStore: (pinia?: Pinia) => any
  createPriceObject: (product: Product) => Promise<PdpPriceObject>
  getUnitPrice: any
  getSavings: any
  getFirstAvailableStock: any
  isInStock: any
  filters: any
  BACKGROUNDS: Record<string, BackgroundImageSet>
  PDP_CONFIG: string
  PDP_CONFIG_V1: string
}

const _getModules = (() => {
  let cache: PdpModules | undefined

  return async (): Promise<PdpModules> => {
    if (cache !== undefined) return cache

    const [
      { default: TranslationHelper },
      { default: UtilityHelper, BACKGROUNDS },
      { default: ApiController },
      { default: TrackingHelper },
      { COOKIES, PRODUCT_TYPES },
      { default: SiteConfig },
      { default: CookieHelper },
      { useCustomerStore },
      { default: AnalyticsItemFactory },
      { default: GA4Controller },
      { default: filters },
      {
        createPriceObject,
        getUnitPrice,
        getSavings,
        getFirstAvailableStock,
        isInStock,
        PDP_CONFIG,
        PDP_CONFIG_V1
      }
    ] = await Promise.all([
      import('@/services/helpers/TranslationsHelper'),
      import('@/services/helpers/UtilityHelper'),
      import('@/services/ApiController'),
      import('@/services/helpers/TrackingHelper'),
      import('@/constants/GlobalConstants'),
      import('@/services/SiteConfig'),
      import('@/services/helpers/CookieHelper'),
      import('@/stores/customer'),
      import('@/modules/tag_manager/AnalyticsItemFactory'),
      import('@/services/analytics/GA4Controller'),
      import('@/services/Filters'),
      import('@/services/helpers/ProductHelper')
    ])
    cache = {
      UtilityHelper,
      TrackingHelper,
      TranslationHelper,
      ApiController,
      COOKIES,
      PRODUCT_TYPES,
      SiteConfig,
      CookieHelper,
      AnalyticsItemFactory,
      GA4Controller,
      useCustomerStore,
      createPriceObject,
      getUnitPrice,
      getSavings,
      getFirstAvailableStock,
      isInStock,
      filters,
      BACKGROUNDS,
      PDP_CONFIG,
      PDP_CONFIG_V1
    }
    return cache
  }
})()
